import _, {chain, compact, sortBy, values} from 'lodash';
import {connect} from 'react-redux';

import {addQueryToPath} from '../../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {CancelToken} from '../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {CompanyModel} from '../../../../../libs/shared/core/src/models/api/company/companyModel';
import {
  TeammateAccountStatusesEnum,
  TeammateLicenseTypesEnum,
  TeammateModel,
} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {isTeammateNamespace} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {isVisitorTeammate} from '../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {fetchApiAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {NewPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {
  buildTeammateSearchUrl,
  filterMentionableTeammates,
  SearchTeammateSortsEnum,
} from '../../../../helpers/models/teammateHelpers';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {RecipientContactMatchModel} from '../../../../models/api/recipientMatchModel';
import {ApiSearchTeammates} from '../../../../models/api/searchTeammateModel';
import {fetchRecipientContactMatchesAsync} from '../../../../services/recipientMatches';
import {AppState} from '../../../../store/appState';
import {getCanAutocompleteMentionContacts, getCompany} from '../../../../store/data/company/companySelectors';
import {
  ConversationsSelector,
  TeammateIdsSelector,
} from '../../../../store/data/conversations/conversationsSelectors';
import {getUserOwnedInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {
  getMentionableEmails,
  makeGetIsTeamMemberForConversations,
  makeGetMentionableTeammatesForNamespace,
} from '../../../../store/data/teammates/mentionTeammatesSelectors';
import {
  getFindTeammate,
  getUser,
  TeammatesSelector,
} from '../../../../store/data/teammates/teammatesSelectors';
import {DropdownFetcher} from '../../dropdowns/source/asyncDropdownSource';
import {MentionEditorDropdownContent} from './mentionEditorDropdownContent';
import {
  MentionDropdownItemTypesEnum,
  MentionDropdownTypedRowValue,
  TeammateRow,
  VisitorRow,
} from './mentionEditorDropdownTypes';

/*
 * Constants.
 */

const resultsLimit = 10;
const guestDiscoverabilityUpdatesMaxContactResults = 5;

/*
 * Props.
 */

interface MentionEditorDropdownContentContainerProps {
  panelId: NewPanelId;
  conversation: ConversationModel | undefined;
}

/*
 * Selectors.
 */

function getConversation(_state: AppState, props: MentionEditorDropdownContentContainerProps) {
  return props.conversation;
}

function makeGetConversations() {
  return createSelector([getConversation], (conversation) => compact([conversation]));
}

function getConversationNamespace(_state: unknown, props: MentionEditorDropdownContentContainerProps) {
  return props.conversation?.namespace;
}

function makeGetConversationInboxIds() {
  return createSelector([getConversation, getUserOwnedInboxes], (conversation, userOwnedInboxes) => {
    if (!conversation) {
      return [];
    }

    if (isTeammateNamespace(conversation.namespace)) {
      return userOwnedInboxes.map((inbox) => inbox.id);
    }

    return conversation.inboxIds;
  });
}

function makeGetTeammateIdsInConversations(
  getConversations: ConversationsSelector<MentionEditorDropdownContentContainerProps>,
) {
  return createSelector([getConversations], (conversations) =>
    chain(conversations)
      .flatMap((c) => values(c.trackers))
      .map((t) => t?.teammateId)
      .compact()
      .uniq()
      .value(),
  );
}

function makeGetSortedMentionableTeammatesForConversations(
  getTeammateIdsInConversations: TeammateIdsSelector<MentionEditorDropdownContentContainerProps>,
  getMentionableTeammates: TeammatesSelector<MentionEditorDropdownContentContainerProps>,
) {
  return createSelector(
    [getTeammateIdsInConversations, getMentionableTeammates],
    (teammateIdsInConversation, mentionableTeammates) =>
      sortBy(mentionableTeammates, (teammate) => !teammateIdsInConversation.includes(teammate.id)),
  );
}

function makeGetTrackerTeammatesForConversations(
  getTeammateIdsInConversations: TeammateIdsSelector<MentionEditorDropdownContentContainerProps>,
) {
  return createSelector(
    [getTeammateIdsInConversations, getFindTeammate],
    (teammateIdsInConversation, findTeammate) =>
      _(teammateIdsInConversation)
        .map((id) => findTeammate(id))
        .compact()
        .value(),
  );
}

function makeGetFetcher(
  getMentionableTeammatesForConversations: TeammatesSelector<MentionEditorDropdownContentContainerProps>,
  getTrackerTeammatesForConversations: TeammatesSelector<MentionEditorDropdownContentContainerProps>,
) {
  return createSelector(
    [
      getCompany,
      getMentionableEmails,
      getMentionableTeammatesForConversations,
      getTrackerTeammatesForConversations,
      getCanAutocompleteMentionContacts,
      getUser,
      makeGetConversationInboxIds(),
    ],
    (
      company,
      mentionableEmails,
      mentionableTeammates,
      trackerTeammates,
      canAutocompleteMentionContacts,
      user,
      conversationInboxIds,
    ): DropdownFetcher<MentionDropdownTypedRowValue> =>
      async (query, _items, cancelToken) => {
        // If the query is empty, just update the state.
        if (!query) {
          return {items: [], canLoadMore: false};
        }

        return fetchSearchResultsForLazyTeammatesAsync(
          query,
          trackerTeammates,
          canAutocompleteMentionContacts,
          company,
          user,
          conversationInboxIds,
          cancelToken,
        );
      },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<MentionEditorDropdownContentContainerProps>()(() => {
  const getConversations = makeGetConversations();
  const getTeammateIdsInConversations = makeGetTeammateIdsInConversations(getConversations);
  const getMentionableTeammates = makeGetMentionableTeammatesForNamespace(getConversationNamespace);
  const getSortedMentionableTeammates = makeGetSortedMentionableTeammatesForConversations(
    getTeammateIdsInConversations,
    getMentionableTeammates,
  );
  const getTrackerTeammatesForConversations = makeGetTrackerTeammatesForConversations(
    getTeammateIdsInConversations,
  );

  return {
    company: getCompany,
    isTeamMember: makeGetIsTeamMemberForConversations(getConversations),
    fetcher: makeGetFetcher(getSortedMentionableTeammates, getTrackerTeammatesForConversations),
    mentionableTeammates: getMentionableTeammates,
  };
});

export const MentionEditorDropdownContentContainer = connect(mapStateToProps)(MentionEditorDropdownContent);

/*
 * Helpers.
 */

function getContactResultsLimit(teammates: ReadonlyArray<TeammateModel>) {
  const visitorTeammatesCount = teammates.filter(isVisitorTeammate).length;
  return Math.min(
    guestDiscoverabilityUpdatesMaxContactResults - visitorTeammatesCount,
    resultsLimit - teammates.length,
  );
}
function groupTeammateRows(teammateRows: ReadonlyArray<TeammateRow>) {
  const groupedTeammateRows: {
    nonVisitors: ReadonlyArray<TeammateRow>;
    visitors: ReadonlyArray<TeammateRow>;
  } = {
    nonVisitors: [],
    visitors: [],
  };
  teammateRows.forEach((teammateRow) => {
    if (isVisitorTeammate(teammateRow.value)) {
      groupedTeammateRows.visitors = [...groupedTeammateRows.visitors, teammateRow];
    } else {
      groupedTeammateRows.nonVisitors = [...groupedTeammateRows.nonVisitors, teammateRow];
    }
  });
  return groupedTeammateRows;
}

function buildDropdownItems(
  teammates: ReadonlyArray<TeammateModel>,
  visitors: ReadonlyArray<RecipientContactMatchModel>,
): ReadonlyArray<MentionDropdownTypedRowValue> {
  const teammateRows: ReadonlyArray<TeammateRow> = teammates.map((value) => ({
    type: MentionDropdownItemTypesEnum.TEAMMATE,
    value,
  }));

  const visitorRows: ReadonlyArray<VisitorRow> = visitors.map((value) => ({
    type: MentionDropdownItemTypesEnum.VISITOR,
    value,
  }));

  // If a visitor has already been mentioned, they will be returned as a teammate with a license type of
  // visitor but we still show them under guests header
  const groupedTeammateRows = groupTeammateRows(teammateRows);

  // We only add the headers if there's items for that section
  let teammateRowsWithHeader: ReadonlyArray<MentionDropdownTypedRowValue> = [];
  if (groupedTeammateRows.nonVisitors.length > 0) {
    teammateRowsWithHeader = [
      {type: MentionDropdownItemTypesEnum.TEAMMATE_HEADER, value: undefined},
      ...groupedTeammateRows.nonVisitors,
    ];
  }

  const allVisitorRows = [...groupedTeammateRows.visitors, ...visitorRows];
  if (allVisitorRows.length > 0) {
    return [
      ...teammateRowsWithHeader,
      {type: MentionDropdownItemTypesEnum.VISITOR_HEADER, value: undefined},
      ...allVisitorRows,
    ];
  }

  return teammateRowsWithHeader;
}

async function fetchSearchResultsForLazyTeammatesAsync(
  query: string,
  trackerTeammates: ReadonlyArray<TeammateModel>,
  canAutocompleteMentionContacts: boolean,
  company: CompanyModel,
  user: TeammateModel,
  conversationInboxIds: ReadonlyArray<number>,
  cancelToken: CancelToken,
): Promise<ReturnType<DropdownFetcher<MentionDropdownTypedRowValue>>> {
  // Locally find the teammates that match the criteria.
  const aliasOrEmailQuery = query.slice(1);
  const matchingTrackers = filterMentionableTeammates(trackerTeammates, aliasOrEmailQuery, resultsLimit);

  if (isVisitorTeammate(user)) {
    return {
      items: buildDropdownItems(matchingTrackers, []),
      canLoadMore: false,
    };
  }

  // If it's just the @ prefix, fetch the suggestions.
  if (query === '@') {
    const suggestions = await fetchSuggestionsAsync({
      cancelToken,
      userUrl: user.url,
      conversationInboxIds,
    });
    const teammates = _(matchingTrackers)
      .concat(suggestions)
      .uniqBy((t) => t.id)
      .take(5)
      .value();

    return {
      items: buildDropdownItems(teammates, []),
      canLoadMore: false,
    };
  }

  // Fetch teammates.
  const url = buildTeammateSearchUrl(company.url, query, {
    limit: resultsLimit,
    accountStatuses: [TeammateAccountStatusesEnum.ACTIVE],
    licenseTypes: [TeammateLicenseTypesEnum.FULL],
    excludedTeammateIds: [user.id],
    sort: SearchTeammateSortsEnum.SCORING,
  });
  const fetchedTeammates = await fetchApiAsync(ApiSearchTeammates, url, {cancelToken});
  const teammates = _(matchingTrackers)
    .concat(fetchedTeammates.teammates)
    .uniqBy((t) => t.id)
    .take(resultsLimit)
    .value();

  // Check if we have enough teammates, or if we need contacts on top.
  if (teammates.length >= resultsLimit || !canAutocompleteMentionContacts) {
    return {
      items: buildDropdownItems(teammates, []),
      canLoadMore: false,
    };
  }

  // Otherwise, fetch additional contacts. Take enough to reach the overall limit.
  const contactResultsLimit = getContactResultsLimit(teammates);
  const contacts = await fetchRecipientContactMatchesAsync(company.url, aliasOrEmailQuery, {
    limit: contactResultsLimit,
    excludeEmails: new Set(teammates.map((t) => t.email)),
    cancelToken,
  });

  return {
    items: buildDropdownItems(teammates, contacts),
    canLoadMore: false,
  };
}

interface SuggestionsFetchOptions {
  cancelToken: CancelToken;
  conversationInboxIds: ReadonlyArray<number>;
  userUrl: string;
}

async function fetchSuggestionsAsync(options: SuggestionsFetchOptions) {
  if (options.conversationInboxIds.length === 0) {
    // We don't have inbox ids to fetch the recent suggestions for.
    return [];
  }

  const recentAssigneesUrl = addQueryToPath(`${options.userUrl}/recent_assignees`, {
    inbox_ids: options.conversationInboxIds.join(','),
  });
  const searchTeammates = await fetchApiAsync(ApiSearchTeammates, recentAssigneesUrl, {
    cancelToken: options.cancelToken,
  });
  return searchTeammates.teammates;
}
